import { createMaterialsApi } from "@/lib/materials-api";

/** 聊天区媒体候选 / 素材预览：避免重复下载，并限制并发。 */
const MAX_CONCURRENT = 4;

export type MaterialsContentPurpose = "content" | "preview";

type CacheEntry = {
  promise: Promise<string>;
  objectUrl?: string;
};

type FetchBlob = (
  accountId: string,
  itemId: number,
  purpose: MaterialsContentPurpose,
) => Promise<Blob>;

const cache = new Map<string, CacheEntry>();
let active = 0;
const waitQueue: Array<() => void> = [];

const defaultFetchBlob: FetchBlob = (accountId, itemId, purpose) => {
  const api = createMaterialsApi(accountId);
  return purpose === "preview" ? api.fetchItemPreview(itemId) : api.fetchItemContent(itemId);
};

let fetchBlobImpl: FetchBlob = defaultFetchBlob;

function cacheKey(accountId: string, itemId: number, purpose: MaterialsContentPurpose): string {
  return `${purpose}:${accountId}:${itemId}`;
}

function releaseSlot(): void {
  active = Math.max(0, active - 1);
  const next = waitQueue.shift();
  if (next) next();
}

async function withConcurrencyLimit<T>(fn: () => Promise<T>): Promise<T> {
  if (active >= MAX_CONCURRENT) {
    await new Promise<void>((resolve) => {
      waitQueue.push(resolve);
    });
  }
  active += 1;
  try {
    return await fn();
  } finally {
    releaseSlot();
  }
}

/** 若已缓存就绪，同步返回 object URL（避免 Dialog 重挂载闪「加载中」）。 */
export function peekMaterialContentObjectUrl(
  accountId: string,
  itemId: number,
  purpose: MaterialsContentPurpose = "content",
): string | null {
  return cache.get(cacheKey(accountId, itemId, purpose))?.objectUrl ?? null;
}

/**
 * 拉取素材并返回可复用的 object URL（模块级缓存，不在组件卸载时 revoke）。
 * purpose=preview 走缩略图接口（图片为 WebP 变体）。
 */
export function fetchMaterialContentObjectUrl(
  accountId: string,
  itemId: number,
  purpose: MaterialsContentPurpose = "content",
): Promise<string> {
  const key = cacheKey(accountId, itemId, purpose);
  const hit = cache.get(key);
  if (hit) return hit.promise;

  const entry: CacheEntry = {
    promise: withConcurrencyLimit(async () => {
      const blob = await fetchBlobImpl(accountId, itemId, purpose);
      const objectUrl = URL.createObjectURL(blob);
      entry.objectUrl = objectUrl;
      return objectUrl;
    }).catch((err) => {
      cache.delete(key);
      throw err;
    }),
  };
  cache.set(key, entry);
  return entry.promise;
}

/** 测试用：替换底层 blob 拉取 */
export function setMaterialsContentBlobFetcherForTests(fn: FetchBlob | null): void {
  fetchBlobImpl = fn ?? defaultFetchBlob;
}

/** 测试用：清空缓存并释放 object URL */
export function clearMaterialsContentCacheForTests(): void {
  for (const entry of cache.values()) {
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
  }
  cache.clear();
  active = 0;
  waitQueue.length = 0;
}

export function materialsContentCacheSizeForTests(): number {
  return cache.size;
}

export function materialsContentMaxConcurrentForTests(): number {
  return MAX_CONCURRENT;
}
